import React, { useImperativeHandle } from 'react';
import { useFormik, FormikProvider, FieldArray } from 'formik';

// components
import FormSectionHeader from '../../../components/forms/FormSectionHeader/FormSectionHeader.component';
import InputField from '../../../components/forms/InputField/InputField.component';
import EmergencyContactInput from '../../../components/forms/lists/EmergencyContactInput/EmergencyContactInput.component';
import ActionButton from '../../../components/buttons/ActionButton/ActionButton.component';
import CertificationInput from '../../../components/forms/lists/CertificationInput/CertificationInput.component';
import Checkbox from '../../../components/forms/Checkbox/Checkbox.component';
import SelectField from '../../../components/forms/SelectField/SelectField.component';

// mui
import { Grid, Divider } from '@mui/material';
import moment from 'moment';

const stateOptions = [
  { label: "Arizona", value: "AZ" },
  { label: "California", value: "CA" },
  { label: "Nevada", value: "NV" },
];


const classificationOptions = [
  { label: 'Superintendent', value: 'SUPERINTENDENT' },
  { label: 'Foreman', value: 'FOREMAN' },
  { label: 'Journeyman', value: 'JOURNEYMAN' },
  { label: 'A8', value: 'A8' },
  { label: 'A7', value: 'A7' },
  { label: 'A6', value: 'A6' },
  { label: 'A5', value: 'A5' },
  { label: 'A4', value: 'A4' },
  { label: 'A3', value: 'A3' },
  { label: 'A2', value: 'A2' },
  { label: 'A1', value: 'A1' },
  { label: 'Yard', value: 'YARD' },
  { label: 'Admin', value: 'ADMIN' },
  { label: 'Clerk', value: 'CLERK' },
];

const AddCrewMember = React.forwardRef(
  ({ stage, errors = {}, foremanUsers = [], loadingForemanUsers = false }, ref) => {
    const formik = useFormik({
      initialValues: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        classification: 'JOURNEYMAN',
        isForeman: false,
        contactPhone: '',
        contactEmail: '',
        street1: '',
        street2: '',
        city: '',
        state: 'CA',
        zipcode: '',
        emergencyContacts: [],
        certifications: [],
        userId: '', // stored as string in the form
      },
      onSubmit: (values) => {
        alert(JSON.stringify(values, null, 2));
      },
    });

    // CRITICAL: convert userId (string) -> Int or null before sending to backend
    useImperativeHandle(ref, () => ({
      getData: () => {
        const data = { ...formik.values };

        if (data.userId === '' || data.userId === null || data.userId === undefined) {
          data.userId = null;
        } else {
          const parsed = parseInt(data.userId, 10);
          data.userId = Number.isNaN(parsed) ? null : parsed;
        }

        return data;
      },
    }));

    // Foreman dropdown options: None + all foreman users
    const foremanOptions = [
      { label: 'None', value: '' },
      ...(foremanUsers || []),
    ];

    const stage0 = (
      <>
        <FormSectionHeader text="Requirements" />
        <p>Please make sure you complete the following before adding a new Crew Member:</p>
        <ul>
          <li>Application</li>
          <li>Union status</li>
          <li>Classification</li>
          <li>Verification & Copy of ID</li>
        </ul>

        <FormSectionHeader text="Name" />

        <InputField
          id="firstName"
          placeholder="First"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.firstName}
          isValid={errors['firstName']}
        />

        <InputField
          id="lastName"
          placeholder="Last"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.lastName}
          isValid={errors['lastName']}
        />

        <FormSectionHeader text="Contact" />

        <InputField
          id="phone"
          placeholder="Phone"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.phone}
          required={false}
          isValid={errors['phone']}
        />

        <InputField
          id="email"
          placeholder="Email"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.email}
          required={false}
          isValid={errors['email']}
        />

        {/* Foreman Users Dropdown */}
        <>
          <FormSectionHeader text="Assign to User Account" />
          <SelectField
            id="userId"
            label="Select Foreman User"
            options={foremanOptions}
            // SelectField will pass selected option object here
            handleChange={(opt) =>
              formik.setFieldValue('userId', opt?.value ?? '')
            }
            // SelectField expects the primitive value; it finds the option internally
            value={formik.values.userId}
            required={false}
            isValid={errors['userId']}
            disabled={loadingForemanUsers}
          />
          {loadingForemanUsers && (
            <p
              style={{
                color: '#666',
                fontSize: '14px',
                marginTop: '-10px',
                marginBottom: '20px',
              }}
            >
              Loading foreman users...
            </p>
          )}
          {!loadingForemanUsers && (!foremanUsers || foremanUsers.length === 0) && (
            <p
              style={{
                color: '#999',
                fontSize: '14px',
                marginTop: '-10px',
                marginBottom: '20px',
              }}
            >
              No foreman users available
            </p>
          )}
        </>

        <FormSectionHeader text="Address" />

        <InputField
          id="street1"
          placeholder="Street"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.street1}
          required={false}
          isValid={errors['street1']}
        />

        <InputField
          id="street2"
          placeholder="Suite/Apt #"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.street2}
          required={false}
          isValid={errors['street2']}
        />

        <InputField
          id="city"
          placeholder="City"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.city}
          required={false}
          isValid={errors['city']}
        />

        <SelectField
          id="state"
          label="State"
          options={stateOptions}
          handleChange={(opt) =>
            formik.setFieldValue('state', opt?.value ?? 'CA')
          }
          value={formik.values.state}
          required={false}
          isValid={errors['state']}
        />

        <InputField
          id="zipcode"
          placeholder="Zipcode"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.zipcode}
          required={false}
          isValid={errors['zipcode']}
        />
      </>
    );

    const stage1 = (
      <>
        <SelectField
          id="classification"
          label="Classification"
          options={classificationOptions}
          handleChange={(opt) =>
            formik.setFieldValue(
              'classification',
              opt?.value ?? 'JOURNEYMAN'
            )
          }
          value={formik.values.classification}
        />

        <Checkbox
          label="Is a Foreman"
          type="text"
          id="isForeman"
          isChecked={formik.values.isForeman}
          handleChange={formik.handleChange}
        />

        <Divider sx={{ marginTop: 2 }} />

        <FieldArray
          name="emergencyContacts"
          render={({ remove, push }) => (
            <div>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <FormSectionHeader text="Emergency Contacts" />
                </Grid>
                <Grid item xs={6} textAlign="right" sx={{ marginTop: 2 }}>
                  <ActionButton
                    label="Add"
                    color="secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      push({
                        firstName: '',
                        lastName: '',
                        phone: '',
                        isPrimary: false,
                      });
                    }}
                  />
                </Grid>
              </Grid>

              {formik.values.emergencyContacts.map((contact, index) => (
                <EmergencyContactInput
                  key={index}
                  prefix={`emergencyContacts.${index}`}
                  handleChange={formik.handleChange}
                  firstName={contact.firstName}
                  lastName={contact.lastName}
                  phone={contact.phone}
                  isPrimary={contact.isPrimary}
                  handleRemove={(e) => {
                    e.preventDefault();
                    remove(index);
                  }}
                  errors={
                    errors['emergencyContacts'].length > index
                      ? errors['emergencyContacts'][index]
                      : {}
                  }
                />
              ))}
            </div>
          )}
        />

        <Divider sx={{ marginTop: 2 }} />

        <FieldArray
          name="certifications"
          render={({ remove, push }) => (
            <div>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <FormSectionHeader text="Certifications" />
                </Grid>
                <Grid item xs={6} textAlign="right" sx={{ marginTop: 2 }}>
                  <ActionButton
                    label="Add"
                    color="secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      push({ name: '', expirationDate: moment() });
                    }}
                  />
                </Grid>
              </Grid>

              {formik.values.certifications.map((certification, index) => (
                <CertificationInput
                  key={index}
                  prefix={`certifications.${index}`}
                  handleChange={formik.handleChange}
                  handleDateChange={(value) =>
                    formik.setFieldValue(
                      `certifications.${index}.expirationDate`,
                      value
                    )
                  }
                  name={certification.name}
                  expirationDate={certification.expirationDate}
                  handleRemove={(e) => {
                    e.preventDefault();
                    remove(index);
                  }}
                  errors={
                    errors['certifications'].length > index
                      ? errors['certifications'][index]
                      : {}
                  }
                />
              ))}
            </div>
          )}
        />
      </>
    );

    return (
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          {stage === 0 && stage0}
          {stage === 1 && stage1}
        </form>
      </FormikProvider>
    );
  }
);

export default AddCrewMember;
